import React, { useState, useEffect, useCallback, useRef } from 'react';
import chatService from '../services/chatService';

/**
 * Chat & Conversations Dashboard
 * 
 * Comprehensive chat management including:
 * - Conversation threads
 * - Plan management
 * - Task tracking
 * - Export to Jira
 */

const CONVERSATION_STATUS = {
  active: { color: '#10b981', label: 'Active', icon: '🟢' },
  resolved: { color: '#6b7280', label: 'Resolved', icon: '✅' },
  pending: { color: '#f59e0b', label: 'Pending', icon: '⏳' },
  archived: { color: '#9ca3af', label: 'Archived', icon: '📁' }
};

const TASK_PRIORITY = {
  low: { color: '#6b7280', label: 'Low' },
  medium: { color: '#f59e0b', label: 'Medium' },
  high: { color: '#ef4444', label: 'High' },
  urgent: { color: '#dc2626', label: 'Urgent' }
};

const ChatDashboard = () => {
  // State
  const [activeTab, setActiveTab] = useState('conversations');
  const [conversations, setConversations] = useState([]);
  const [plans, setPlans] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Modal states
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  // Form states
  const [planForm, setPlanForm] = useState({
    name: '',
    description: '',
    goals: [],
    timeline: ''
  });

  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    assignee: '',
    due_date: '',
    plan_id: ''
  });

  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [conversationsData, plansData, tasksData] = await Promise.all([
        chatService.getConversations().catch(() => ({ conversations: [] })),
        chatService.getPlans().catch(() => ({ plans: [] })),
        chatService.getTasks().catch(() => ({ tasks: [] }))
      ]);

      setConversations(conversationsData.conversations || conversationsData || []);
      setPlans(plansData.plans || plansData || []);
      setTasks(tasksData.tasks || tasksData || []);
    } catch (err) {
      console.error('Error fetching chat data:', err);
      setError('Failed to load chat data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedConversation?.messages]);

  // Create conversation
  const handleStartConversation = async () => {
    if (!newMessage.trim()) return;
    
    setLoading(true);
    try {
      const response = await chatService.createConversation({
        title: newMessage.substring(0, 50),
        initial_message: newMessage
      });
      setConversations([response, ...conversations]);
      setSelectedConversation(response);
      setNewMessage('');
      setSuccess('Conversation started');
    } catch (err) {
      setError('Failed to start conversation');
    } finally {
      setLoading(false);
    }
  };

  // Send message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    try {
      const message = {
        content: newMessage,
        sender: 'user',
        timestamp: new Date().toISOString()
      };
      
      // Optimistic update
      setSelectedConversation({
        ...selectedConversation,
        messages: [...(selectedConversation.messages || []), message]
      });
      setNewMessage('');

      // Send to server
      await chatService.sendMessage(selectedConversation.id, { content: newMessage });
    } catch (err) {
      setError('Failed to send message');
    }
  };

  // Create plan
  const handleCreatePlan = async () => {
    setLoading(true);
    try {
      const response = await chatService.createPlan(planForm);
      setPlans([response, ...plans]);
      setShowPlanModal(false);
      setSuccess('Plan created successfully');
      setPlanForm({ name: '', description: '', goals: [], timeline: '' });
    } catch (err) {
      setError('Failed to create plan');
    } finally {
      setLoading(false);
    }
  };

  // Create task
  const handleCreateTask = async () => {
    setLoading(true);
    try {
      const response = await chatService.createTask(taskForm);
      setTasks([response, ...tasks]);
      setShowTaskModal(false);
      setSuccess('Task created successfully');
      setTaskForm({ title: '', description: '', priority: 'medium', assignee: '', due_date: '', plan_id: '' });
    } catch (err) {
      setError('Failed to create task');
    } finally {
      setLoading(false);
    }
  };

  // Convert to task
  const handleConvertToTask = async (conversationId) => {
    setLoading(true);
    try {
      const response = await chatService.convertToTask(conversationId);
      setTasks([response, ...tasks]);
      setSuccess('Conversation converted to task');
    } catch (err) {
      setError('Failed to convert to task');
    } finally {
      setLoading(false);
    }
  };

  // Export to Jira
  const handleExportToJira = async (taskId) => {
    setLoading(true);
    try {
      await chatService.exportToJira(taskId);
      setSuccess('Task exported to Jira');
      setShowExportModal(false);
    } catch (err) {
      setError('Failed to export to Jira');
    } finally {
      setLoading(false);
    }
  };

  // Render conversation list
  const renderConversationList = () => (
    <div className="conversation-list">
      <div className="list-header">
        <h3>Conversations</h3>
        <span className="count">{conversations.length}</span>
      </div>
      <div className="list-items">
        {conversations.map(conv => {
          const status = CONVERSATION_STATUS[conv.status] || CONVERSATION_STATUS.active;
          return (
            <div 
              key={conv.id}
              className={`conversation-item ${selectedConversation?.id === conv.id ? 'selected' : ''}`}
              onClick={() => setSelectedConversation(conv)}
            >
              <div className="conv-avatar">
                {conv.title?.charAt(0) || '?'}
              </div>
              <div className="conv-content">
                <div className="conv-header">
                  <span className="conv-title">{conv.title || 'Untitled'}</span>
                  <span className="conv-time">
                    {new Date(conv.updated_at || conv.created_at || Date.now()).toLocaleDateString()}
                  </span>
                </div>
                <p className="conv-preview">
                  {conv.last_message || conv.messages?.[conv.messages.length - 1]?.content || 'No messages'}
                </p>
                <div className="conv-meta">
                  <span className="status-badge" style={{ background: status.color }}>
                    {status.icon} {status.label}
                  </span>
                  {conv.messages?.length > 0 && (
                    <span className="message-count">{conv.messages.length} messages</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {conversations.length === 0 && (
          <div className="empty-list">
            <span>💬</span>
            <p>No conversations yet</p>
          </div>
        )}
      </div>
    </div>
  );

  // Render chat view
  const renderChatView = () => (
    <div className="chat-view">
      {selectedConversation ? (
        <>
          <div className="chat-header">
            <div className="chat-info">
              <h3>{selectedConversation.title || 'Untitled Conversation'}</h3>
              <span className="chat-status">
                {CONVERSATION_STATUS[selectedConversation.status]?.icon}
                {CONVERSATION_STATUS[selectedConversation.status]?.label}
              </span>
            </div>
            <div className="chat-actions">
              <button 
                className="btn btn-sm btn-secondary"
                onClick={() => handleConvertToTask(selectedConversation.id)}
              >
                📋 Create Task
              </button>
              <button className="btn btn-sm btn-secondary">
                📁 Archive
              </button>
            </div>
          </div>
          <div className="messages-container">
            {(selectedConversation.messages || []).map((msg, idx) => (
              <div 
                key={idx}
                className={`message ${msg.sender === 'user' ? 'outgoing' : 'incoming'}`}
              >
                <div className="message-content">
                  <p>{msg.content}</p>
                  <span className="message-time">
                    {new Date(msg.timestamp || Date.now()).toLocaleTimeString()}
                  </span>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
          <div className="message-input">
            <input
              type="text"
              value={newMessage}
              onChange={e => setNewMessage(e.target.value)}
              placeholder="Type a message..."
              onKeyPress={e => e.key === 'Enter' && handleSendMessage()}
            />
            <button className="btn btn-primary" onClick={handleSendMessage}>
              📤 Send
            </button>
          </div>
        </>
      ) : (
        <div className="no-conversation">
          <span style={{ fontSize: '4rem' }}>💬</span>
          <h3>Select a conversation</h3>
          <p>or start a new one</p>
          <div className="start-conversation">
            <input
              type="text"
              value={newMessage}
              onChange={e => setNewMessage(e.target.value)}
              placeholder="Start a new conversation..."
              onKeyPress={e => e.key === 'Enter' && handleStartConversation()}
            />
            <button className="btn btn-primary" onClick={handleStartConversation}>
              Start
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // Render plans tab
  const renderPlansTab = () => (
    <div className="plans-container">
      <div className="plans-header">
        <h3>Marketing Plans</h3>
        <button className="btn btn-primary" onClick={() => setShowPlanModal(true)}>
          ➕ New Plan
        </button>
      </div>
      <div className="plans-grid">
        {plans.map(plan => (
          <div key={plan.id} className="plan-card">
            <div className="plan-header">
              <h4>{plan.name}</h4>
              <span className="plan-timeline">{plan.timeline}</span>
            </div>
            <p className="plan-description">{plan.description}</p>
            {plan.goals?.length > 0 && (
              <div className="plan-goals">
                <h5>Goals:</h5>
                <ul>
                  {plan.goals.map((goal, idx) => (
                    <li key={idx}>{goal}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="plan-stats">
              <span>
                📋 {tasks.filter(t => t.plan_id === plan.id).length} tasks
              </span>
              <span>
                ✅ {tasks.filter(t => t.plan_id === plan.id && t.status === 'completed').length} completed
              </span>
            </div>
            <div className="plan-actions">
              <button 
                className="btn btn-sm btn-primary"
                onClick={() => {
                  setSelectedPlan(plan);
                  setTaskForm({ ...taskForm, plan_id: plan.id });
                  setShowTaskModal(true);
                }}
              >
                ➕ Add Task
              </button>
              <button className="btn btn-sm btn-secondary">
                ✏️ Edit
              </button>
            </div>
          </div>
        ))}
        {plans.length === 0 && (
          <div className="empty-state">
            <span style={{ fontSize: '4rem' }}>📋</span>
            <h3>No plans yet</h3>
            <p>Create your first marketing plan</p>
            <button className="btn btn-primary" onClick={() => setShowPlanModal(true)}>
              ➕ Create Plan
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // Render tasks tab
  const renderTasksTab = () => (
    <div className="tasks-container">
      <div className="tasks-header">
        <h3>Tasks</h3>
        <div className="tasks-actions">
          <button 
            className="btn btn-secondary"
            onClick={() => setShowExportModal(true)}
          >
            📤 Export to Jira
          </button>
          <button className="btn btn-primary" onClick={() => setShowTaskModal(true)}>
            ➕ New Task
          </button>
        </div>
      </div>
      <div className="tasks-list">
        <table className="table">
          <thead>
            <tr>
              <th>Task</th>
              <th>Plan</th>
              <th>Priority</th>
              <th>Assignee</th>
              <th>Due Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tasks.map(task => {
              const priority = TASK_PRIORITY[task.priority] || TASK_PRIORITY.medium;
              const plan = plans.find(p => p.id === task.plan_id);
              return (
                <tr key={task.id}>
                  <td>
                    <div className="task-info">
                      <span className="task-title">{task.title}</span>
                      <span className="task-description">{task.description?.substring(0, 50)}</span>
                    </div>
                  </td>
                  <td>{plan?.name || '-'}</td>
                  <td>
                    <span 
                      className="priority-badge"
                      style={{ background: priority.color }}
                    >
                      {priority.label}
                    </span>
                  </td>
                  <td>{task.assignee || 'Unassigned'}</td>
                  <td>{task.due_date ? new Date(task.due_date).toLocaleDateString() : '-'}</td>
                  <td>
                    <span className={`status-badge ${task.status}`}>
                      {task.status || 'pending'}
                    </span>
                  </td>
                  <td>
                    <button 
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleExportToJira(task.id)}
                    >
                      📤
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {tasks.length === 0 && (
          <div className="empty-table">
            <span style={{ fontSize: '3rem' }}>📋</span>
            <p>No tasks yet</p>
          </div>
        )}
      </div>
    </div>
  );

  // Render plan modal
  const renderPlanModal = () => (
    <div className="modal-overlay" onClick={() => setShowPlanModal(false)}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Create Marketing Plan</h3>
          <button className="btn-icon" onClick={() => setShowPlanModal(false)}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label>Plan Name</label>
            <input
              type="text"
              className="form-control"
              value={planForm.name}
              onChange={e => setPlanForm({ ...planForm, name: e.target.value })}
              placeholder="e.g., Q1 Marketing Campaign"
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              className="form-control"
              rows={3}
              value={planForm.description}
              onChange={e => setPlanForm({ ...planForm, description: e.target.value })}
              placeholder="Describe the plan objectives..."
            />
          </div>
          <div className="form-group">
            <label>Timeline</label>
            <input
              type="text"
              className="form-control"
              value={planForm.timeline}
              onChange={e => setPlanForm({ ...planForm, timeline: e.target.value })}
              placeholder="e.g., Jan - Mar 2024"
            />
          </div>
          <div className="form-group">
            <label>Goals (one per line)</label>
            <textarea
              className="form-control"
              rows={3}
              value={planForm.goals.join('\n')}
              onChange={e => setPlanForm({ ...planForm, goals: e.target.value.split('\n').filter(Boolean) })}
              placeholder="Enter each goal on a new line..."
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => setShowPlanModal(false)}>
            Cancel
          </button>
          <button 
            className="btn btn-primary"
            onClick={handleCreatePlan}
            disabled={!planForm.name}
          >
            ➕ Create Plan
          </button>
        </div>
      </div>
    </div>
  );

  // Render task modal
  const renderTaskModal = () => (
    <div className="modal-overlay" onClick={() => setShowTaskModal(false)}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Create Task</h3>
          <button className="btn-icon" onClick={() => setShowTaskModal(false)}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label>Task Title</label>
            <input
              type="text"
              className="form-control"
              value={taskForm.title}
              onChange={e => setTaskForm({ ...taskForm, title: e.target.value })}
              placeholder="e.g., Create social media content"
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              className="form-control"
              rows={3}
              value={taskForm.description}
              onChange={e => setTaskForm({ ...taskForm, description: e.target.value })}
              placeholder="Task details..."
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Priority</label>
              <select
                className="form-control"
                value={taskForm.priority}
                onChange={e => setTaskForm({ ...taskForm, priority: e.target.value })}
              >
                {Object.entries(TASK_PRIORITY).map(([key, val]) => (
                  <option key={key} value={key}>{val.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Due Date</label>
              <input
                type="date"
                className="form-control"
                value={taskForm.due_date}
                onChange={e => setTaskForm({ ...taskForm, due_date: e.target.value })}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Plan</label>
            <select
              className="form-control"
              value={taskForm.plan_id}
              onChange={e => setTaskForm({ ...taskForm, plan_id: e.target.value })}
            >
              <option value="">No plan</option>
              {plans.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Assignee</label>
            <input
              type="text"
              className="form-control"
              value={taskForm.assignee}
              onChange={e => setTaskForm({ ...taskForm, assignee: e.target.value })}
              placeholder="Assignee name or email"
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => setShowTaskModal(false)}>
            Cancel
          </button>
          <button 
            className="btn btn-primary"
            onClick={handleCreateTask}
            disabled={!taskForm.title}
          >
            ➕ Create Task
          </button>
        </div>
      </div>
    </div>
  );

  // Render export modal
  const renderExportModal = () => (
    <div className="modal-overlay" onClick={() => setShowExportModal(false)}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>📤 Export to Jira</h3>
          <button className="btn-icon" onClick={() => setShowExportModal(false)}>✕</button>
        </div>
        <div className="modal-body">
          <p className="text-secondary">Select tasks to export to Jira:</p>
          <div className="export-list">
            {tasks.filter(t => !t.jira_id).map(task => (
              <label key={task.id} className="export-item">
                <input type="checkbox" />
                <span>{task.title}</span>
                <span className="task-priority" style={{ background: TASK_PRIORITY[task.priority]?.color }}>
                  {TASK_PRIORITY[task.priority]?.label}
                </span>
              </label>
            ))}
          </div>
          {tasks.filter(t => !t.jira_id).length === 0 && (
            <p className="text-center text-secondary">No tasks to export</p>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => setShowExportModal(false)}>
            Cancel
          </button>
          <button className="btn btn-primary">
            📤 Export Selected
          </button>
        </div>
      </div>
    </div>
  );

  if (loading && !conversations.length) {
    return (
      <div className="page-container">
        <div className="loading-spinner">Loading Chat Dashboard...</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1>💬 Chat & Tasks</h1>
          <p className="text-secondary">Manage conversations, plans, and tasks</p>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="alert alert-danger mb-4">
          {error}
          <button className="btn-icon ml-auto" onClick={() => setError(null)}>✕</button>
        </div>
      )}
      {success && (
        <div className="alert alert-success mb-4">
          {success}
          <button className="btn-icon ml-auto" onClick={() => setSuccess(null)}>✕</button>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs mb-4">
        {['conversations', 'plans', 'tasks'].map(tab => (
          <button
            key={tab}
            className={`tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'conversations' && '💬 Conversations'}
            {tab === 'plans' && '📋 Plans'}
            {tab === 'tasks' && '✅ Tasks'}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'conversations' && (
        <div className="chat-container">
          {renderConversationList()}
          {renderChatView()}
        </div>
      )}

      {activeTab === 'plans' && renderPlansTab()}
      {activeTab === 'tasks' && renderTasksTab()}

      {/* Modals */}
      {showPlanModal && renderPlanModal()}
      {showTaskModal && renderTaskModal()}
      {showExportModal && renderExportModal()}

      <style>{`
        .tabs {
          display: flex;
          gap: 0.5rem;
          border-bottom: 2px solid var(--gray-200);
        }
        .tab {
          padding: 0.75rem 1.5rem;
          border: none;
          background: none;
          cursor: pointer;
          color: var(--gray-600);
          border-bottom: 2px solid transparent;
          margin-bottom: -2px;
        }
        .tab.active {
          color: var(--primary-600);
          border-bottom-color: var(--primary-600);
          font-weight: 600;
        }
        .chat-container {
          display: grid;
          grid-template-columns: 350px 1fr;
          gap: 1rem;
          height: calc(100vh - 250px);
          min-height: 500px;
        }
        .conversation-list {
          background: white;
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow-sm);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .list-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          border-bottom: 1px solid var(--gray-200);
        }
        .list-header h3 {
          margin: 0;
        }
        .count {
          background: var(--primary-100);
          color: var(--primary-700);
          padding: 0.25rem 0.5rem;
          border-radius: 10px;
          font-size: 0.75rem;
          font-weight: 600;
        }
        .list-items {
          flex: 1;
          overflow-y: auto;
        }
        .conversation-item {
          display: flex;
          gap: 1rem;
          padding: 1rem;
          border-bottom: 1px solid var(--gray-100);
          cursor: pointer;
          transition: background 0.2s;
        }
        .conversation-item:hover {
          background: var(--gray-50);
        }
        .conversation-item.selected {
          background: var(--primary-50);
          border-left: 3px solid var(--primary-500);
        }
        .conv-avatar {
          width: 40px;
          height: 40px;
          background: var(--primary-100);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          color: var(--primary-700);
          flex-shrink: 0;
        }
        .conv-content {
          flex: 1;
          min-width: 0;
        }
        .conv-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 0.25rem;
        }
        .conv-title {
          font-weight: 500;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .conv-time {
          font-size: 0.75rem;
          color: var(--gray-500);
          flex-shrink: 0;
        }
        .conv-preview {
          font-size: 0.875rem;
          color: var(--gray-600);
          margin: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .conv-meta {
          display: flex;
          gap: 0.5rem;
          align-items: center;
          margin-top: 0.5rem;
        }
        .status-badge {
          padding: 0.125rem 0.5rem;
          border-radius: 10px;
          font-size: 0.7rem;
          color: white;
        }
        .message-count {
          font-size: 0.7rem;
          color: var(--gray-500);
        }
        .chat-view {
          background: white;
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow-sm);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .chat-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          border-bottom: 1px solid var(--gray-200);
        }
        .chat-info h3 {
          margin: 0;
        }
        .chat-status {
          font-size: 0.875rem;
          color: var(--gray-600);
        }
        .chat-actions {
          display: flex;
          gap: 0.5rem;
        }
        .messages-container {
          flex: 1;
          overflow-y: auto;
          padding: 1rem;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        .message {
          max-width: 70%;
          display: flex;
        }
        .message.outgoing {
          margin-left: auto;
        }
        .message.incoming {
          margin-right: auto;
        }
        .message-content {
          padding: 0.75rem 1rem;
          border-radius: 18px;
        }
        .message.outgoing .message-content {
          background: var(--primary-500);
          color: white;
          border-bottom-right-radius: 4px;
        }
        .message.incoming .message-content {
          background: var(--gray-100);
          border-bottom-left-radius: 4px;
        }
        .message-content p {
          margin: 0;
        }
        .message-time {
          font-size: 0.7rem;
          opacity: 0.7;
          display: block;
          text-align: right;
          margin-top: 0.25rem;
        }
        .message-input {
          display: flex;
          gap: 0.5rem;
          padding: 1rem;
          border-top: 1px solid var(--gray-200);
        }
        .message-input input {
          flex: 1;
          padding: 0.75rem 1rem;
          border: 1px solid var(--gray-300);
          border-radius: 25px;
        }
        .no-conversation {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 2rem;
        }
        .no-conversation h3 {
          margin: 1rem 0 0.5rem;
        }
        .start-conversation {
          display: flex;
          gap: 0.5rem;
          width: 100%;
          max-width: 400px;
          margin-top: 1rem;
        }
        .start-conversation input {
          flex: 1;
          padding: 0.75rem 1rem;
          border: 1px solid var(--gray-300);
          border-radius: 25px;
        }
        .empty-list {
          text-align: center;
          padding: 2rem;
          color: var(--gray-500);
        }
        .empty-list span {
          font-size: 2rem;
        }
        .plans-header, .tasks-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
        }
        .tasks-actions {
          display: flex;
          gap: 0.5rem;
        }
        .plans-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 1rem;
        }
        .plan-card {
          background: white;
          border-radius: var(--radius-lg);
          padding: 1.5rem;
          box-shadow: var(--shadow-sm);
        }
        .plan-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 1rem;
        }
        .plan-header h4 {
          margin: 0;
        }
        .plan-timeline {
          font-size: 0.8rem;
          color: var(--gray-500);
        }
        .plan-description {
          color: var(--gray-600);
          font-size: 0.9rem;
          margin-bottom: 1rem;
        }
        .plan-goals {
          margin-bottom: 1rem;
        }
        .plan-goals h5 {
          margin: 0 0 0.5rem;
          font-size: 0.875rem;
        }
        .plan-goals ul {
          margin: 0;
          padding-left: 1.25rem;
        }
        .plan-goals li {
          font-size: 0.875rem;
          color: var(--gray-600);
        }
        .plan-stats {
          display: flex;
          gap: 1rem;
          margin-bottom: 1rem;
          font-size: 0.875rem;
          color: var(--gray-500);
        }
        .plan-actions {
          display: flex;
          gap: 0.5rem;
        }
        .tasks-container {
          background: white;
          border-radius: var(--radius-lg);
          padding: 1.5rem;
          box-shadow: var(--shadow-sm);
        }
        .task-info {
          display: flex;
          flex-direction: column;
        }
        .task-title {
          font-weight: 500;
        }
        .task-description {
          font-size: 0.8rem;
          color: var(--gray-500);
        }
        .priority-badge {
          display: inline-block;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.75rem;
          color: white;
        }
        .status-badge.completed {
          background: #10b981;
          color: white;
        }
        .status-badge.pending {
          background: #f59e0b;
          color: white;
        }
        .status-badge.in-progress {
          background: #3b82f6;
          color: white;
        }
        .empty-state {
          text-align: center;
          padding: 4rem;
          background: var(--gray-50);
          border-radius: var(--radius-lg);
          grid-column: 1 / -1;
        }
        .empty-state h3 {
          margin: 1rem 0 0.5rem;
        }
        .empty-table {
          text-align: center;
          padding: 3rem;
        }
        .form-row {
          display: flex;
          gap: 1rem;
        }
        .form-row .form-group {
          flex: 1;
        }
        .export-list {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          max-height: 300px;
          overflow-y: auto;
        }
        .export-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 0.75rem;
          background: var(--gray-50);
          border-radius: var(--radius-sm);
          cursor: pointer;
        }
        .export-item:hover {
          background: var(--gray-100);
        }
        .export-item span:first-of-type {
          flex: 1;
        }
        .task-priority {
          padding: 0.125rem 0.5rem;
          border-radius: 4px;
          font-size: 0.7rem;
          color: white;
        }
      `}</style>
    </div>
  );
};

export default ChatDashboard;
